import { FileDto, FolderMinimalDto } from "@braoshzmvavz/picnube-common";
import {
  Container,
  Loader,
  LoadingOverlay,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiFolder } from "react-icons/hi";
import { useAppSelector } from "../../app/hooks";
import useApi from "../../services/useApi";
import useApiDemo from "../../services/useApiDemo";
import { selectCurrentSpace } from "../../state/profile/profileSlice";
import Gallery from "../gallery/Gallery";
import TimeRangeFilter from "./TimeRangeFilter";
import { Settings } from "src/settings";
import { useMediaQuery } from "@mantine/hooks";

interface TimelineProps {
  demo?: boolean;
}

interface TimelineSection {
  dateKey: string; // e.g., "September 10, 2025"
  files: FileDto[];
  folderIds: Set<string>;
  folderInfos: { id: string; name: string }[];
}

export default function Timeline({ demo = false }: TimelineProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const currentSpace = useAppSelector(selectCurrentSpace);
  const [files, setFiles] = useState<FileDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [locale, setLocale] = useState<string>("en-US"); // TODO change locale dynamically
  const [folderNamesMap, setFolderNamesMap] = useState<Map<string, string>>(
    new Map()
  );
  const [filters, setFilters] = useState<any>(null);

  const { searchFiles: searchFilesReal, findFoldersByIdList } = useApi();
  const { searchFiles: searchFilesDemo } = useApiDemo();

  const loadingRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scrollbar month anchors and state
  const monthAnchorRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeMonthKey, setActiveMonthKey] = useState<string | null>(null);
  const monthButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Sticky header refs
  const sectionHeaderRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const searchFiles = (spaceId: string, page: number) => {
    if (demo) {
      return searchFilesDemo(spaceId, page);
    } else if (filters != null && !!filters?.start && !!filters?.end) {
      return searchFilesReal(spaceId, page, 30, filters);
    } else {
      return searchFilesReal(spaceId, page, 30, null);
    }
  };

  // Fetch photos when component mounts or space changes
  useEffect(() => {
    if (!currentSpace) return;

    setIsLoading(true);
    setPage(0);
    setHasMore(true);
    searchFiles(currentSpace.id, 0)
      .then((response: FileDto[]) => {
        console.log("Timeline photos:", response);
        setFiles(response);
        setIsLoading(false);
        setInitialized(true);

        // Check if there are more pages
        if (response.length === 0) {
          setHasMore(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching timeline photos:", error);
        setIsLoading(false);
        setInitialized(true);
        setHasMore(false);
      });
  }, [currentSpace, demo, filters]);

  // Trust backend ordering: no EXIF parsing or client-side resorting

  // Load more photos function
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !currentSpace) return;

    setIsLoadingMore(true);
    const nextPage = page + 1;

    try {
      const response = await searchFiles(currentSpace.id, nextPage);
      console.log(`Timeline photos page ${nextPage}:`, response);

      if (response.length === 0) {
        setHasMore(false);
      } else {
        setFiles((prevFiles) => [...prevFiles, ...response]);
        setPage(nextPage);
      }
    } catch (error) {
      console.error("Error loading more timeline photos:", error);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, currentSpace, page, searchFiles]);

  // Infinite scroll implementation
  useEffect(() => {
    if (!hasMore) return;

    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    } as IntersectionObserverInit;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        loadMore();
      }
    }, options);

    const target = loadingRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
      observer.disconnect();
    };
  }, [hasMore, files, loadMore]);

  // Build sequential sections by date, preserving API order
  const sections = useMemo<TimelineSection[]>(() => {
    const sectionList: TimelineSection[] = [];
    for (const file of files) {
      const rawDateString =
        file.exifCreationDate ?? file.folderDate ?? file.dateCreated;
      const date = new Date(rawDateString);
      const dateKey = date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const last = sectionList[sectionList.length - 1];
      if (last && last.dateKey === dateKey) {
        last.files.push(file);
        last.folderIds.add(file.folderId);
      } else {
        sectionList.push({
          dateKey,
          files: [file],
          folderIds: new Set([file.folderId]),
          folderInfos: [],
        });
      }
    }

    // Update folder names for each section
    sectionList.forEach((section) => {
      section.folderInfos = Array.from(section.folderIds)
        .map((folderId) => {
          return {
            id: folderId,
            name:
              folderNamesMap.get(folderId) ||
              `Folder ${folderId.slice(0, 8)}...`,
          };
        })
        .sort();
    });

    return sectionList;
  }, [files, folderNamesMap]);

  // Build month groups (YYYY-MM) in the order encountered from API
  const sortedMonths = useMemo(() => {
    const seen: Set<string> = new Set();
    const months: {
      key: string;
      label: string;
      year: number;
      monthIndex: number;
    }[] = [];
    for (const section of sections) {
      const d = new Date(section.dateKey);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      if (!seen.has(monthKey)) {
        seen.add(monthKey);
        months.push({
          key: monthKey,
          label: d.toLocaleDateString(undefined, {
            month: "short",
            year: "numeric",
          }),
          year: d.getFullYear(),
          monthIndex: d.getMonth(),
        });
      }
    }
    return months;
  }, [sections]);

  // Fetch folder names for new folders
  useEffect(() => {
    if (!currentSpace || !initialized || sections.length === 0) return;

    const allFolderIds = new Set<string>();
    files.forEach((file) => allFolderIds.add(file.folderId));

    const missingFolderIds = Array.from(allFolderIds).filter(
      (id) => !folderNamesMap.has(id)
    );

    if (missingFolderIds.length === 0) return;

    // Fetch folder names for missing folders
    findFoldersByIdList(missingFolderIds).then(
      (response: FolderMinimalDto[]) => {
        setFolderNamesMap((prev) => {
          const newMap = new Map(prev);
          response.forEach(({ id, name }) => {
            newMap.set(id, name);
          });
          return newMap;
        });
      }
    );
  }, [sections]);

  // Map each month to the first dateKey entry that belongs to it
  const firstDateKeyByMonth = useMemo(() => {
    const map: Record<string, string> = {};
    for (const section of sections) {
      const d = new Date(section.dateKey);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      if (!map[monthKey]) {
        map[monthKey] = section.dateKey;
      }
    }
    return map;
  }, [sections]);

  // Observe month anchors to highlight active month in rail
  useEffect(() => {
    if (sortedMonths.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Choose the top-most intersecting month as active
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          const monthKey = (visible[0].target as HTMLDivElement).dataset[
            "monthKey"
          ];
          if (monthKey) setActiveMonthKey(monthKey);
        } else {
          // Fallback: find the one closest to top
          let closest: { key: string; delta: number } | null = null;
          for (const m of sortedMonths) {
            const el = monthAnchorRefs.current[m.key];
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            const delta = Math.abs(rect.top);
            if (closest === null || delta < closest.delta) {
              closest = { key: m.key, delta };
            }
          }
          if (closest) setActiveMonthKey(closest.key);
        }
      },
      {
        root: null,
        rootMargin: "0px 0px -70% 0px",
        threshold: 0.01,
      }
    );

    sortedMonths.forEach((m) => {
      const el = monthAnchorRefs.current[m.key];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sortedMonths]);

  // Reset month anchors when months change
  useEffect(() => {
    monthAnchorRefs.current = {};
  }, [sortedMonths]);

  const scrollToMonth = (monthKey: string) => {
    const el = monthAnchorRefs.current[monthKey];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Keep the active month visible in the mobile scroller
  useEffect(() => {
    if (!activeMonthKey) return;
    const btn = monthButtonRefs.current[activeMonthKey];
    if (btn && btn.parentElement) {
      const parent = btn.parentElement;
      const parentRect = parent.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      if (btnRect.left < parentRect.left || btnRect.right > parentRect.right) {
        btn.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }
  }, [activeMonthKey]);

  const formatDateTitle = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return dateString;
    }
  };

  const onFilterChange = (start: Date, end: Date) => {
    setFilters({
      start,
      end,
    });
  };

  return (
    <Container
      size="lg"
      py="lg"
      className="relative min-h-full"
      ref={containerRef}
    >
      {isLoading && (
        <LoadingOverlay
          visible={true}
          zIndex={400}
          overlayProps={{
            blur: "xl",
            color: "black",
            opacity: "70%",
          }}
          loaderProps={{ color: "blue", type: "oval", size: "md" }}
        />
      )}

      <div className="flex">
        <TimeRangeFilter onChange={onFilterChange}></TimeRangeFilter>
      </div>

      {!isLoading && initialized && (
        <div className="flex flex-col gap-8 pt-4">
          {sections.map((section, index) => {
            const dateObj = new Date(section.dateKey);
            const monthKey = `${dateObj.getFullYear()}-${String(
              dateObj.getMonth() + 1
            ).padStart(2, "0")}`;
            const isFirstOfMonth =
              firstDateKeyByMonth[monthKey] === section.dateKey;

            return (
              <div key={section.dateKey} className="flex flex-col">
                {/* Invisible month anchor at first day section for each month */}
                {isFirstOfMonth && (
                  <div
                    ref={(el) => {
                      monthAnchorRefs.current[monthKey] = el;
                    }}
                    data-month-key={monthKey}
                    className="h-0"
                  />
                )}
                {/* Date Header */}
                <div
                  ref={(el) => {
                    sectionHeaderRefs.current[section.dateKey] = el;
                  }}
                  data-section-key={section.dateKey}
                  className={`sticky top-0 z-40 flex flex-col gap-2 bg-white py-3 px-4 mb-4 
                  dark:bg-[var(--mantine-color-dark-7)]`}
                >
                  <Title
                    order={2}
                    size="h3"
                    className="text-gray-600 dark:text-gray-400"
                  >
                    {formatDateTitle(section.dateKey)}
                  </Title>
                  <Text size="sm" c="dimmed">
                    {section.folderInfos.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {section.folderInfos.map((f) => {
                          return (
                            <div key={f.id}>
                              <a
                                className="cursor-pointer flex justify-start items-center gap-1 w-fit px-2 py-1 dark:hover:bg-dark-999 hover:bg-grey-400 rounded-lg"
                                href={`/browse/${f.id}`}
                              >
                                <HiFolder
                                  className="min-w-fit"
                                  size={20}
                                ></HiFolder>
                                {f.name}
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Text>
                </div>

                {/* Gallery Section */}
                <div className="">
                  <Gallery
                    folders={[]}
                    files={section.files}
                    loadMoreFn={() => {}} // No load more for individual galleries
                    onFolderClickFn={() => {}}
                    onActionClick={() => {}}
                    isSelectionEnabled={false}
                    hiddenOptions={true}
                    isMiniGallery={true}
                    enableInfiniteLoading={false}
                    demo={demo}
                    hardcodeSettings={
                      {
                        photoAlbum_layout: "rows",
                        photoAlbum_spacing: 8,
                        photoAlbum_targetRowHeight: 200,
                        photoAlbum_roundedBorder: true,
                        slideshow_delay: 5000,
                      } as Settings
                    }
                  />
                </div>
              </div>
            );
          })}

          {/* Infinite scroll trigger element and loading indicator */}
          {hasMore && (
            <div
              ref={loadingRef}
              className="flex justify-center items-center py-8"
            >
              {isLoadingMore ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader color="blue" size="md"></Loader>
                  <Text size="sm" c="dimmed">
                    Loading more photos...
                  </Text>
                </div>
              ) : (
                <div className="h-20 pointer-events-none"></div>
              )}
            </div>
          )}

          {/* End of timeline indicator */}
          {!hasMore && files.length > 0 && (
            <div className="flex justify-center items-center py-8">
              <Text size="sm" c="dimmed">
                You've reached the end of your timeline
              </Text>
            </div>
          )}

          {/* Right-side month/year scroll rail */}
          {false && sortedMonths.length > 0 && (
            <div className="hidden md:flex fixed right-4 top-1/2 -translate-y-1/2 flex-col items-center gap-2 z-[350] select-none">
              <div className="flex flex-col items-center justify-between bg-gray-200/70 dark:bg-gray-700/60 rounded-full py-3 px-2 h-[70vh]">
                {sortedMonths.map((m, idx) => {
                  const isActive = activeMonthKey === m.key;
                  const isYearChange =
                    idx === 0 || m.year !== sortedMonths[idx - 1].year;
                  return (
                    <button
                      key={m.key}
                      onClick={() => scrollToMonth(m.key)}
                      className={`relative group w-2 ${
                        isYearChange ? "h-4" : "h-3"
                      } rounded-full transition-colors ${
                        isActive
                          ? "bg-blue-500"
                          : "bg-gray-400 dark:bg-gray-500 hover:bg-gray-500 dark:hover:bg-gray-400"
                      }`}
                      aria-label={m.label}
                      title={m.label}
                    >
                      {/* Year label bubble on hover and for active */}
                      <div
                        className={`absolute -left-2 -translate-x-full top-1/2 -translate-y-1/2 whitespace-nowrap text-xs px-2 py-0.5 rounded bg-gray-800 text-white opacity-0 group-hover:opacity-100 ${
                          isActive ? "!opacity-100" : ""
                        }`}
                      >
                        {m.label}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* TODO: Mobile: bottom horizontal month scroller */}
          {/*sortedMonths.length > 0 && (
            <div className="md:hidden fixed left-1/2 -translate-x-1/2 bottom-4 z-[20] select-none w-[92vw]">
              <div className="backdrop-blur bg-gray-900/60 dark:bg-gray-800/70 text-white rounded-full px-2 py-2 overflow-x-auto no-scrollbar">
                <div className="flex items-center gap-1">
                  {sortedMonths.map((m) => {
                    const isActive = activeMonthKey === m.key;
                    // Short label like: Jan '25
                    const shortLabel = new Date(
                      m.year,
                      m.monthIndex,
                      1
                    ).toLocaleDateString(locale, {
                      month: "short",
                      year: "2-digit",
                    });
                    return (
                      <button
                        key={m.key}
                        ref={(el) => {
                          monthButtonRefs.current[m.key] = el;
                        }}
                        onClick={() => scrollToMonth(m.key)}
                        className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                          isActive
                            ? "bg-blue-500 text-white"
                            : "bg-white/10 text-white hover:bg-white/20"
                        }`}
                        aria-label={m.label}
                        title={m.label}
                      >
                        {shortLabel}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )*/}
        </div>
      )}

      {!isLoading && initialized && sections.length === 0 && (
        <Text size="lg" c="dimmed" ta="center" mt="xl">
          No content found
        </Text>
      )}
    </Container>
  );
}
